import { useEffect } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import {
  Alert,
  ContentLoader,
  DropdownField,
  Heading,
  Modal,
  NakedButton,
  Text,
  useFormField,
  useModalControl,
} from '@frontend/design-system';
import * as styles from '../AppointmentTypeSettings/styles';

interface Props {
  numOfApptTypes: number;
  apptReqConfigIsLoading: boolean;
  apptReqConfigFetchError: boolean;
  minOpeningsPermitted: number;
  updateMinOpenings: (minOpeningsPermitted: number) => void;
  setReqNumInputValue: (inputValue: number) => void;
}

export const RequiredApptTimes = ({
  numOfApptTypes,
  apptReqConfigIsLoading,
  apptReqConfigFetchError,
  minOpeningsPermitted,
  updateMinOpenings,
  setReqNumInputValue,
}: Props) => {
  const { t } = useTranslation('schedule');
  const helpModal = useModalControl();
  const fieldProps = useFormField(
    {
      type: 'dropdown',
      value: `${minOpeningsPermitted ?? 1}`,
    },
    [minOpeningsPermitted]
  );

  useEffect(() => {
    setReqNumInputValue(Number(fieldProps.value));
  }, []);

  useEffect(() => {
    const initialValue = minOpeningsPermitted ?? 1;
    const fieldValue = Number(fieldProps.value);
    if (initialValue !== fieldValue) {
      updateMinOpenings(fieldValue);
    }
  }, [fieldProps.value]);

  return (
    <>
      <form name={t('required number of appointment times')}>
        <Heading level={3} css={styles.cardTextSpacing}>
          <NakedButton {...helpModal.triggerProps} css={css({ textAlign: 'left' })}>
            {t('Required Number Of Appt. Times')} <Icon name='help' size={14} />
          </NakedButton>
        </Heading>

        {apptReqConfigFetchError && (
          <Alert type='error' css={styles.alertMargin}>
            {t('Unable to fetch required number of appointment times. Please try again later.')}
          </Alert>
        )}

        {numOfApptTypes === 0 && (
          <Alert type='warning' css={styles.alertMargin}>
            {t('Please add an appointment type before updating the required number of appointment times.')}
          </Alert>
        )}

        {!apptReqConfigIsLoading && (
          <DropdownField
            {...fieldProps}
            css={styles.requiredApptTimeDropdown}
            name='appt-times-dropdown'
            label={t('Required Times')}
          >
            <DropdownField.Option value='1' searchValue={t('One')}>
              {t('One')}
            </DropdownField.Option>
            <DropdownField.Option value='2' searchValue={t('Two')}>
              {t('Two')}
            </DropdownField.Option>
            <DropdownField.Option value='3' searchValue={t('Three')}>
              {t('Three')}
            </DropdownField.Option>
          </DropdownField>
        )}
      </form>

      <ContentLoader show={apptReqConfigIsLoading} />

      <Modal {...helpModal.modalProps}>
        <Modal.Header>{t('Required Number Of Appointment Times')}</Modal.Header>
        <Modal.Body>
          <Text color='light' css={styles.cardTextSpacing}>
            {t(
              'Select the number of different appointment times (max of 3) a patient must select in order to submit an appointment request.'
            )}
          </Text>
          <Text color='light' css={styles.cardTextSpacing}>
            {t('In many cases, a higher number of requested times gives your office more scheduling flexibility.')}
          </Text>
        </Modal.Body>
        <Modal.Actions onPrimaryClick={helpModal.closeModal} primaryLabel={t('Done')} />
      </Modal>
    </>
  );
};
